import { Head, Link, router } from "@inertiajs/react"
import { BellOff, CheckCircle2, XCircle } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  orgReportSubscriptionsOverviewPath,
  unsubscribeReportPath,
} from "@/routes"

interface Props {
  subscription: {
    report_title: string
    view_name: string
    recipient: string
    has_user: boolean
    org_slug: string
  } | null
  token?: string
  done?: boolean
}

const invalidState = (
  <>
    <XCircle className="text-muted-foreground mb-4 h-12 w-12" />
    <h1 className="mb-2 text-2xl font-semibold">Link not found</h1>
    <p className="text-muted-foreground">
      This unsubscribe link is invalid or has already been used. The
      subscription it referred to may have been removed.
    </p>
  </>
)

// GET renders this confirmation; the actual flip is the POST below so that
// mail-scanner link prefetches can never silently kill a subscription.
export default function Unsubscribed({ subscription, token, done }: Props) {
  const [submitting, setSubmitting] = useState(false)

  return (
    <>
      <Head title="Unsubscribe" />
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
        {!subscription ? (
          invalidState
        ) : done ? (
          <>
            <CheckCircle2 className="mb-4 h-12 w-12 text-green-600" />
            <h1 className="mb-2 text-2xl font-semibold">
              You&apos;re unsubscribed
            </h1>
            <p className="text-muted-foreground">
              {subscription.recipient} will no longer receive the{" "}
              <strong>
                {subscription.report_title} — {subscription.view_name}
              </strong>{" "}
              report.
            </p>
            {subscription.has_user && (
              <Button asChild variant="outline" className="mt-6 h-11">
                <Link
                  href={orgReportSubscriptionsOverviewPath(
                    subscription.org_slug,
                  )}
                >
                  Manage subscriptions in app
                </Link>
              </Button>
            )}
          </>
        ) : token ? (
          <>
            <BellOff className="text-muted-foreground mb-4 h-12 w-12" />
            <h1 className="mb-2 text-2xl font-semibold">
              Unsubscribe from {subscription.view_name}?
            </h1>
            <p className="text-muted-foreground">
              {subscription.recipient} will stop receiving the{" "}
              <strong>
                {subscription.report_title} — {subscription.view_name}
              </strong>{" "}
              report.
            </p>
            <Button
              className="mt-6 h-11"
              disabled={submitting}
              onClick={() =>
                router.post(
                  unsubscribeReportPath(token),
                  {},
                  {
                    onStart: () => setSubmitting(true),
                    onFinish: () => setSubmitting(false),
                  },
                )
              }
            >
              Unsubscribe
            </Button>
          </>
        ) : (
          invalidState
        )}
      </div>
    </>
  )
}
